import { AppModule } from '@app/app.module';
import {
	OrderDirection,
	TableDateFilter,
	TableFilter,
	TableHeader,
	TableInputFilter,
	TableSelectFilter,
} from '@beego/ngx-table';
import { ClientOriginProvider } from '@internal-providers/client-origin.provider';
import { TargetAudienceProvider } from '@internal-providers/target-audience.provider';
import { VALIDATION } from '@shared-libs/constants';
import { AgeCategory, Gender, UserPermissions } from '@shared-libs/enums';
import { translate } from '@shared-libs/helpers';
import { UserManager } from '@shared-managers/user.manager';

export class ClientTableFactory {
	public static getHeaders(): Array<TableHeader> {
		return [
			new TableHeader(translate('CLIENT.TITLE'), ['firstname', OrderDirection.asc], 3),
			new TableHeader(translate('CLIENT.ADDRESS'), ['Address', 'street'], 4),
			new TableHeader(translate('CLIENT.CONTACT.EMAIL'), ['email'], 2),
			new TableHeader(translate('CLIENT.CONTACT.PHONE'), ['telephone'], 2),
			new TableHeader(translate('CLIENT.CONTACT.LANDLINE'), ['landline'], 2),
			new TableHeader(),
		];
	}

	public static getFilters(): { [key: string]: TableFilter } {
		const userManager = AppModule.injector.get(UserManager);
		const targetAudienceProvider = AppModule.injector.get(TargetAudienceProvider);
		const clientOriginProvider = AppModule.injector.get(ClientOriginProvider);

		return {
			clientFirstnameFilter: new TableInputFilter({
				property: 'name',
				name: translate('FILTERS.FIRSTNAME_NAME.TITLE'),
				placeholder: translate('FILTERS.FIRSTNAME_NAME.PLACEHOLDER'),
			}),
			phoneFilter: new TableInputFilter({
				property: 'phone',
				name: translate('FILTERS.PHONE.TITLE'),
				placeholder: translate('FILTERS.PHONE.PLACEHOLDER'),
			}),
			emailFilter: new TableInputFilter({
				property: 'email',
				name: translate('FILTERS.EMAIL.TITLE'),
				placeholder: translate('FILTERS.EMAIL.PLACEHOLDER'),
			}),
			birthDateFilter: new TableDateFilter({
				property: 'birthDate',
				name: translate('FILTERS.BIRTH_DATE.TITLE'),
			}),
			ageCategoryFilter: new TableSelectFilter({
				property: 'ageCategory',
				data: Object.values(AgeCategory).map((value) => ({
					name: value,
					value,
				})),
				name: translate('FILTERS.AGE_CATEGORY.TITLE'),
				resource: 'local',
			}),
			socialFilter: new TableInputFilter({
				property: 'social',
				name: translate('FILTERS.SOCIAL.TITLE'),
				placeholder: translate('FILTERS.SOCIAL.PLACEHOLDER'),
				allowed: userManager.hasAllPermissions([UserPermissions.ReadClientsSocial]),
			}),
			genderFilter: new TableSelectFilter({
				property: 'gender',
				data: Object.values(Gender).map((value) => ({
					name: value,
					value,
				})),
				name: translate('FILTERS.GENDER.TITLE'),
				resource: 'local',
			}),
			digibankTargetAudienceFilter: new TableSelectFilter({
				property: 'digibankTargetAudience',
				data: async () =>
					(await targetAudienceProvider.getAll()).map((targetAudience) => ({
						name: targetAudience.name,
						value: targetAudience.name,
					})),
				name: translate('FILTERS.TARGET_AUDIENCE.TITLE'),
				resource: 'online',
			}),
			gdprFilter: new TableSelectFilter({
				property: 'gdpr',
				data: [
					{ name: translate('YES'), value: true },
					{ name: translate('NO'), value: false },
				],
				name: translate('FILTERS.GDPR.TITLE'),
				resource: 'local',
			}),
			ClientOriginIdFilter: new TableSelectFilter({
				property: 'ClientOriginId',
				data: async () =>
					(await clientOriginProvider.getAll()).map((clientOrigin) => ({
						name: clientOrigin.name,
						value: clientOrigin.id,
					})),
				name: translate('FILTERS.CLIENT_ORIGIN.TITLE'),
				resource: 'online',
			}),
			zipcodeFilter: new TableInputFilter({
				property: 'Address.zipcode',
				name: translate('FILTERS.CLIENT_ZIPCODE.TITLE'),
				placeholder: translate('FILTERS.CLIENT_ZIPCODE.PLACEHOLDER'),
				validation: VALIDATION.ZIPCODE_REGEX,
			}),
			hasActiveLoansFilter: new TableSelectFilter({
				property: 'hasActiveLoans',
				data: [
					{ name: translate('YES'), value: true },
					{ name: translate('NO'), value: false },
				],
				name: translate('FILTERS.HAS_ACTIVE_LOANS.TITLE'),
				resource: 'local',
			}),
			hasBeegoAppointmentFilter: new TableSelectFilter({
				property: 'hasBeegoAppointment',
				data: [
					{ name: translate('YES'), value: true },
					{ name: translate('NO'), value: false },
				],
				name: translate('FILTERS.HAS_BEEGO_APPOINTMENT.TITLE'),
				resource: 'local',
			}),
			hasAttendancesFilter: new TableSelectFilter({
				property: 'hasAttendances',
				data: [
					{ name: translate('YES'), value: true },
					{ name: translate('NO'), value: false },
				],
				name: translate('FILTERS.HAS_ATTENDANCES.TITLE'),
				resource: 'local',
			}),
			hasReservationForEventFilter: new TableInputFilter({
				property: 'hasReservationForEvent',
				name: translate('FILTERS.HAS_RESERVATION_FOR_EVENT.TITLE'),
				placeholder: translate('FILTERS.HAS_RESERVATION_FOR_EVENT.PLACEHOLDER'),
			}),
		};
	}
}
